import React from 'react';
import Icon from '../AppIcon';

const ProgressIndicator = ({ 
  currentStep = 0, 
  steps = [], 
  isLoading = false,
  className = '' 
}) => {
  const defaultSteps = [
    { label: 'Capturing Screenshot', description: 'Taking webpage snapshot' },
    { label: 'Uploading to IPFS', description: 'Storing on decentralized network' },
    { label: 'Creating Transaction', description: 'Preparing blockchain record' },
    { label: 'Confirming on Chain', description: 'Finalizing notarization' }
  ];

  const processSteps = steps?.length > 0 ? steps : defaultSteps;

  const getStepStatus = (stepIndex) => {
    if (stepIndex < currentStep) return 'completed';
    if (stepIndex === currentStep) return isLoading ? 'loading' : 'current';
    return 'pending';
  };

  const getStepIcon = (stepIndex, status) => {
    switch (status) {
      case 'completed':
        return <Icon name="CheckCircle" size={20} className="text-success" />;
      case 'loading':
        return <Icon name="Loader2" size={20} className="text-primary animate-spin" />;
      case 'current':
        return <Icon name="Circle" size={20} className="text-primary" />;
      default:
        return <Icon name="Circle" size={20} className="text-muted-foreground" />;
    }
  };

  return (
    <div className={`w-full max-w-2xl mx-auto ${className}`}>
      <div className="space-y-4">
        {processSteps?.map((step, index) => {
          const status = getStepStatus(index);
          const isActive = status === 'current' || status === 'loading';
          
          return (
            <div
              key={index}
              className={`flex items-start space-x-4 p-4 rounded-lg border transition-smooth ${
                isActive 
                  ? 'border-primary bg-primary/5' 
                  : status === 'completed' ?'border-success bg-success/5' :'border-border bg-card'
              }`}
            >
              {/* Step Icon */}
              <div className="flex-shrink-0 mt-0.5">
                {getStepIcon(index, status)}
              </div>
              {/* Step Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <h3 className={`text-sm font-medium ${
                    isActive ? 'text-primary' : 
                    status === 'completed'? 'text-success' : 'text-muted-foreground'
                  }`}>
                    {step?.label}
                  </h3>
                  
                  {/* Step Number */}
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    isActive ? 'bg-primary text-primary-foreground' :
                    status === 'completed' ? 'bg-success text-success-foreground' :
                    'bg-muted text-muted-foreground'
                  }`}>
                    {index + 1}
                  </span>
                </div>
                
                {step?.description && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {step?.description}
                  </p>
                )}

                {/* Progress Bar for Current Step */}
                {status === 'loading' && (
                  <div className="mt-3">
                    <div className="w-full bg-muted rounded-full h-1.5">
                      <div className="bg-primary h-1.5 rounded-full animate-pulse" style={{ width: '60%' }} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {/* Overall Progress */}
      <div className="mt-6 pt-4 border-t border-border">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            Progress: {currentStep} of {processSteps?.length} steps
          </span>
          <span className="font-medium text-foreground">
            {Math.round((currentStep / processSteps?.length) * 100)}% Complete
          </span>
        </div>
        
        <div className="mt-2 w-full bg-muted rounded-full h-2">
          <div 
            className="bg-primary h-2 rounded-full transition-all duration-300 ease-out"
            style={{ width: `${(currentStep / processSteps?.length) * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default ProgressIndicator;